// eslint-disable-next-line max-classes-per-file
import { ColDef, ValueFormatterParams, ValueGetterParams } from '@ag-grid-community/core';
import moment from 'moment';

import { FilterDateFormatType } from '../models/filter-formats';
import {
	formatDimension,
	formatNumber,
	formatTextDimension,
	NumberFormat,
	unformatTextDimension
} from '../utils/number.format';
import { getFormatDate, getParseDate } from '../utils/resolved-format-date';

import { PinOrdersCellComponent } from './cell-renderers/pin-orders-cell.component';
import { FavoriteOrdersCellComponent } from './cell-renderers/favorite-orders-cell.component';
import { IconLinkCellComponent } from './cell-renderers/icon-link-cell.component';
import { IconMethodCellComponent } from './cell-renderers/icon-method-cell.component';
import { IconOpenDocumentCellComponent } from './cell-renderers/icon-open-document-cell.component';
import { LinkCellComponent } from './cell-renderers/link-cell.component';
import { LinkMultiValueCellComponent } from './cell-renderers/link-multi-value-cell.component';
import { MultiLineValueCellComponent } from './cell-renderers/multi-line-value-cell.component';
import { MultiLineValueTypes } from './cell-renderers/multi-line-value-types';
import { NumericCellComponent } from './cell-renderers/numeric-cell.component';
import { NumericPercentageCellComponent } from './cell-renderers/numeric-percentage-cell.component';
import { RadioButtonCellComponent } from './cell-renderers/radiobutton-cell.component';
import { ColDefWithFields, ColumnType } from './column-type';
import { keepOnlyOneBreakableSpace } from './string-utils';
import { CheckboxCellComponent } from './cell-renderers/checkbox-cell.component';
import { IconCellComponent } from './cell-renderers/icon-cell.component';

export class CommonColDef<TData = unknown, TValue = unknown> implements ColDefWithFields<TData, TValue> {
	suppressMovable: NonNullable<ColDef<TData, TValue>['suppressMovable']> = true;
	resizable: NonNullable<ColDef<TData, TValue>['resizable']> = true;
	lockPinned: NonNullable<ColDef<TData, TValue>['lockPinned']> = true;
	minWidth: NonNullable<ColDef<TData, TValue>['minWidth']> = 100;
	suppressHeaderMenuButton: NonNullable<ColDef<TData, TValue>['suppressHeaderMenuButton']> = true;
	sortable: NonNullable<ColDef<TData, TValue>['sortable']> = true;
	lockPosition: NonNullable<ColDef<TData, TValue>['lockPosition']> = true;
	lockVisible: NonNullable<ColDef<TData, TValue>['lockVisible']> = true;
	menuTabs: NonNullable<ColDef<TData, TValue>['menuTabs']> = ['filterMenuTab'];
	headerName: NonNullable<ColDef<TData, TValue>['headerName']>;
	headerTooltip: NonNullable<ColDef<TData, TValue>['headerTooltip']>;

	filter?: ColDef<TData, TValue>['filter'];
	colId?: ColDef<TData, TValue>['colId'];
	sort?: ColDef<TData, TValue>['sort'];
	sortingOrder?: ColDef<TData, TValue>['sortingOrder'];
	field?: ColDef<TData, TValue>['field'];
	type?: ColDef<TData, TValue>['type'];
	hide?: ColDef<TData, TValue>['hide'];
	pinned?: ColDef<TData, TValue>['pinned'];
	tooltipField?: ColDef<TData, TValue>['tooltipField'];
	valueGetter?: ColDef<TData, TValue>['valueGetter'];
	filterValueGetter?: ColDef<TData, TValue>['filterValueGetter'];
	valueSetter?: ColDef<TData, TValue>['valueSetter'];
	keyCreator?: ColDef<TData, TValue>['keyCreator'];
	width?: ColDef<TData, TValue>['width'];
	maxWidth?: ColDef<TData, TValue>['maxWidth'];
	flex?: ColDef<TData, TValue>['flex'];
	autoHeight?: ColDef<TData, TValue>['autoHeight'];
	cellClass?: ColDef<TData, TValue>['cellClass'];
	cellStyle?: ColDef<TData, TValue>['cellStyle'];
	cellRenderer?: ColDef<TData, TValue>['cellRenderer'];
	cellRendererParams?: ColDef<TData, TValue>['cellRendererParams'];
	cellRendererSelector?: ColDef<TData, TValue>['cellRendererSelector'];
	cellEditor?: ColDef<TData, TValue>['cellEditor'];
	cellEditorParams?: ColDef<TData, TValue>['cellEditorParams'];
	cellEditorSelector?: ColDef<TData, TValue>['cellEditorSelector'];
	valueFormatter?: ColDef<TData, TValue>['valueFormatter'];
	valueParser?: ColDef<TData, TValue>['valueParser'];
	aggFunc?: ColDef<TData, TValue>['aggFunc'];
	allowedAggFuncs?: ColDef<TData, TValue>['allowedAggFuncs'];
	rowGroupIndex?: ColDef<TData, TValue>['rowGroupIndex'];
	rowGroup?: ColDef<TData, TValue>['rowGroup'];
	showRowGroup?: ColDef<TData, TValue>['showRowGroup'];
	pivotIndex?: ColDef<TData, TValue>['pivotIndex'];
	pivot?: ColDef<TData, TValue>['pivot'];
	comparator?: ColDef<TData, TValue>['comparator'];
	equals?: ColDef<TData, TValue>['equals'];
	pivotComparator?: ColDef<TData, TValue>['pivotComparator'];
	checkboxSelection?: ColDef<TData, TValue>['checkboxSelection'];
	rowDrag?: ColDef<TData, TValue>['rowDrag'];
	rowDragText?: ColDef<TData, TValue>['rowDragText'];
	unSortIcon?: ColDef<TData, TValue>['unSortIcon'];
	suppressSizeToFit?: ColDef<TData, TValue>['suppressSizeToFit'];
	suppressAutoSize?: ColDef<TData, TValue>['suppressAutoSize'];
	suppressKeyboardEvent?: ColDef<TData, TValue>['suppressKeyboardEvent'];
	enableRowGroup?: ColDef<TData, TValue>['enableRowGroup'];
	enablePivot?: ColDef<TData, TValue>['enablePivot'];
	enableValue?: ColDef<TData, TValue>['enableValue'];
	editable?: ColDef<TData, TValue>['editable'];
	colSpan?: ColDef<TData, TValue>['colSpan'];
	rowSpan?: ColDef<TData, TValue>['rowSpan'];
	suppressPaste?: ColDef<TData, TValue>['suppressPaste'];
	suppressNavigable?: ColDef<TData, TValue>['suppressNavigable'];
	getQuickFilterText?: ColDef<TData, TValue>['getQuickFilterText'];
	singleClickEdit?: ColDef<TData, TValue>['singleClickEdit'];
	filterParams?: ColDef<TData, TValue>['filterParams'];
	cellClassRules?: ColDef<TData, TValue>['cellClassRules'];
	onCellValueChanged?: ColDef<TData, TValue>['onCellValueChanged'];
	onCellClicked?: ColDef<TData, TValue>['onCellClicked'];
	onCellDoubleClicked?: ColDef<TData, TValue>['onCellDoubleClicked'];
	onCellContextMenu?: ColDef<TData, TValue>['onCellContextMenu'];
	icons?: ColDef<TData, TValue>['icons'];
	enableCellChangeFlash?: ColDef<TData, TValue>['enableCellChangeFlash'];
	pivotValueColumn?: ColDef<TData, TValue>['pivotValueColumn'];
	pivotTotalColumnIds?: ColDef<TData, TValue>['pivotTotalColumnIds'];
	headerComponent?: ColDef<TData, TValue>['headerComponent'];
	headerComponentParams?: ColDef<TData, TValue>['headerComponentParams'];
	floatingFilter?: ColDef<TData, TValue>['floatingFilter'];
	floatingFilterComponent?: ColDef<TData, TValue>['floatingFilterComponent'];
	floatingFilterComponentParams?: ColDef<TData, TValue>['floatingFilterComponentParams'];
	refData?: ColDef<TData, TValue>['refData'];
	chartDataType?: ColDef<TData, TValue>['chartDataType'];
	headerValueGetter?: ColDef<TData, TValue>['headerValueGetter'];
	headerClass?: ColDef<TData, TValue>['headerClass'];
	suppressHeaderKeyboardEvent?: ColDef<TData, TValue>['suppressHeaderKeyboardEvent'];
	suppressHeaderFilterButton?: ColDef<TData, TValue>['suppressHeaderFilterButton'];
	suppressHeaderContextMenu?: ColDef<TData, TValue>['suppressHeaderContextMenu'];
	columnGroupShow?: ColDef<TData, TValue>['columnGroupShow'];
	toolPanelClass?: ColDef<TData, TValue>['toolPanelClass'];
	suppressColumnsToolPanel?: ColDef<TData, TValue>['suppressColumnsToolPanel'];
	suppressFiltersToolPanel?: ColDef<TData, TValue>['suppressFiltersToolPanel'];
	tooltipComponent?: ColDef<TData, TValue>['tooltipComponent'];
	tooltipComponentParams?: ColDef<TData, TValue>['tooltipComponentParams'];
	pivotKeys?: ColDef<TData, TValue>['pivotKeys'];
	cellDataType?: ColDef<TData, TValue>['cellDataType'];
	suppressFillHandle?: ColDef<TData, TValue>['suppressFillHandle'];
	initialHide?: ColDef<TData, TValue>['initialHide'];
	useValueFormatterForExport?: ColDef<TData, TValue>['useValueFormatterForExport'];
	cellEditorPopup?: ColDef<TData, TValue>['cellEditorPopup'];
	cellEditorPopupPosition?: ColDef<TData, TValue>['cellEditorPopupPosition'];
	useValueParserForImport?: ColDef<TData, TValue>['useValueParserForImport'];
	wrapHeaderText?: ColDef<TData, TValue>['wrapHeaderText'];
	autoHeaderHeight?: ColDef<TData, TValue>['autoHeaderHeight'];
	initialPinned?: ColDef<TData, TValue>['initialPinned'];
	initialPivot?: ColDef<TData, TValue>['initialPivot'];
	initialPivotIndex?: ColDef<TData, TValue>['initialPivotIndex'];
	wrapText?: ColDef<TData, TValue>['wrapText'];
	initialRowGroup?: ColDef<TData, TValue>['initialRowGroup'];
	initialRowGroupIndex?: ColDef<TData, TValue>['initialRowGroupIndex'];
	initialAggFunc?: ColDef<TData, TValue>['initialAggFunc'];
	defaultAggFunc?: ColDef<TData, TValue>['defaultAggFunc'];
	initialSort?: ColDef<TData, TValue>['initialSort'];
	sortIndex?: ColDef<TData, TValue>['sortIndex'];
	initialSortIndex?: ColDef<TData, TValue>['initialSortIndex'];
	initialWidth?: ColDef<TData, TValue>['initialWidth'];
	initialFlex?: ColDef<TData, TValue>['initialFlex'];
	suppressSpanHeaderHeight?: ColDef<TData, TValue>['suppressSpanHeaderHeight'];

	fields?: string[];
	columnType?: ColumnType;
	valueType?: MultiLineValueTypes;
	isMandatoryOnLeft?: boolean;
	groupId?: string;
	constructor(params?: ColDefWithFields<TData, TValue>) {
		if (params) {
			Object.assign(this, params);
			this.headerName = params.headerName ? keepOnlyOneBreakableSpace(params.headerName) : '';
			this.headerTooltip = params.headerName || '';
		} else {
			this.headerName = '';
			this.headerTooltip = '';
		}
	}

	tooltipValueGetter: NonNullable<ColDef<TData, TValue>['tooltipValueGetter']> = params =>
		params.valueFormatted || <string> params.value;
}

interface Dimensions {
	thickness: number | string;
	width: number | string;
	length?: number | string;
}

type ThreeFieldKeys<TData> = [keyof TData, keyof TData, keyof TData];

export class DimensionColDef<TData = unknown> extends CommonColDef<TData, string> {
	dimensionGetter: (params: ValueGetterParams<TData>) => Dimensions | undefined;
	private readonly fieldList?: ThreeFieldKeys<TData>;

	constructor(
		params: ColDefWithFields<TData, string>
			& { dimensionGetter?: (params: ValueGetterParams<TData>) => (Dimensions | undefined) }
	) {
		super(params);
		this.fieldList = <ThreeFieldKeys<TData> | undefined>(<unknown>params.fields);
		this.dimensionGetter = params.dimensionGetter ?? (() => undefined);
		if (this.fieldList) {
			const [tKey, wKey, lKey] = this.fieldList;
			const fieldMap = {
				thickness: String(tKey),
				width: String(wKey),
				length: String(lKey),
			};
			this.filterParams = { fieldMap };
		} else {
			this.filterParams = {};
		}
	}

	override valueGetter = (params: ValueGetterParams<TData, string>) => {
		const [thickness, width, length] = this.getDimTriplet(params);
		return formatTextDimension(thickness ?? '', width ?? '', length);
	};

	override valueFormatter = (params: ValueFormatterParams<TData, string>) => {
		const value = unformatTextDimension(params.value ?? '');
		return value ? formatDimension(value.thickness, value.width, value.length) : '';
	};

	private getDimTriplet(
		params: ValueGetterParams<TData, string>
	): readonly [string | number | undefined, string | number | undefined, string | number | undefined] {
		const data = <Record<string, unknown> | undefined>(<unknown>params.data);

		if (data && this.fieldList) {
			const [tKey, wKey, lKey] = this.fieldList;
			const t = <string | number | undefined>(data[String(tKey)]);
			const w = <string | number | undefined>(data[String(wKey)]);
			const l = <string | number | undefined>(data[String(lKey)]);
			const form = <string | undefined>(data['form']);
			const isProducedField = String(tKey).startsWith('produced');
			if (isProducedField) {
				const length = form === '04' ? Number(l) / 1000 : '';
				return [t, w, length];
			}
			if (t !== undefined || w !== undefined || l !== undefined) {
				return [t, w, l];
			}
		}

		const dim = this.dimensionGetter(params);
		if (dim) {
			return [dim.thickness, dim.width, dim.length];
		}
		return [undefined, undefined, undefined];
	}
}

export class DateColDef<TData = unknown, TValue = unknown> extends CommonColDef<TData, TValue> {
	declare cellRendererParams: { inputFormat: string; outputFormat: string };
	constructor(params: ColDefWithFields<TData, TValue>) {
		super({
			minWidth: 100,
			maxWidth: 100,
			width: 100,
			resizable: false,
			suppressSizeToFit: true,
			cellRendererParams: { inputFormat: 'isoDate', outputFormat: 'date' },
			...params
		});
	}

	override valueFormatter = (params: ValueFormatterParams) =>
		params.value
			? moment(<string> params.value, getParseDate(this.cellRendererParams.inputFormat))
				.format(getFormatDate(this.cellRendererParams.outputFormat))
			: '';
}

export class NumericColDef<TData = unknown, TValue = unknown> extends CommonColDef<TData, TValue> {
	declare cellRendererParams: { numericFormat: NumberFormat };

	constructor(params: ColDefWithFields<TData, TValue>) {
		super({
			type: 'numericColumn',
			minWidth: 78,
			maxWidth: 120,
			cellRenderer: NumericCellComponent,
			cellRendererParams: { numericFormat: 'generic' },
			...params
		});
	}

	override valueFormatter = (params: ValueFormatterParams) =>
		params.value ? params.colDef.field === 'length' ? <string> params.value : formatNumber(
			typeof params.value === 'number' ? params.value : Number(params.value),
			this.cellRendererParams.numericFormat
		) : '';
}

export class IconLinkColDef<TData = unknown, TValue = unknown> extends CommonColDef<TData, TValue> {
	constructor(params: ColDefWithFields<TData, TValue>) {
		super({
			maxWidth: 32,
			minWidth: 32,
			width: 32,
			suppressSizeToFit: true,
			resizable: false,
			sortable: false,
			cellRenderer: IconLinkCellComponent,
			...params
		});
	}
}

export class IconColDef<TData = unknown, TValue = unknown> extends CommonColDef<TData, TValue> {
	constructor(params: ColDefWithFields<TData, TValue>) {
		super({
			maxWidth: 32,
			minWidth: 32,
			width: 32,
			suppressSizeToFit: true,
			resizable: false,
			sortable: false,
			cellRenderer: IconCellComponent,
			...params
		});
	}
}

export class LinkColDef<TData = unknown, TValue = unknown> extends CommonColDef<TData, TValue> {
	constructor(params: ColDefWithFields<TData, TValue>) {
		super({
			cellRenderer: LinkCellComponent,
			...params
		});
	}
}

export class FavoriteOrdersColDef<TData = unknown, TValue = unknown> extends CommonColDef<TData, TValue> {
	constructor(params: ColDefWithFields<TData, TValue>) {
		super({
			cellRenderer: FavoriteOrdersCellComponent,
			maxWidth: 36,
			minWidth: 36,
			width: 36,
			sortable: false,
			resizable: false,
			pinned: 'left',
			isMandatoryOnLeft: true,
			...params
		});
	}
}

export class PinOrdersColDef<TData = unknown, TValue = unknown> extends CommonColDef<TData, TValue> {
	constructor(params: ColDefWithFields<TData, TValue>) {
		super({
			cellRenderer: PinOrdersCellComponent,
			maxWidth: 36,
			minWidth: 36,
			width: 36,
			sortable: false,
			resizable: false,
			pinned: 'left',
			isMandatoryOnLeft: true,
			...params
		});
	}
}

export class RadioBtnColDef<TData = unknown, TValue = unknown> extends CommonColDef<TData, TValue> {
	constructor(params: ColDefWithFields<TData, TValue>) {
		super({
			cellRenderer: RadioButtonCellComponent,
			...params
		});
	}
}

export class IconRedirectColDef<TData = unknown, TValue = unknown> extends CommonColDef<TData, TValue> {
	constructor(params: ColDefWithFields<TData, TValue>) {
		super({
			maxWidth: 32,
			minWidth: 32,
			width: 32,
			suppressSizeToFit: true,
			resizable: false,
			sortable: false,
			cellRenderer: IconOpenDocumentCellComponent,
			...params
		});
	}
}

export class NumericPercentageColDef<TData = unknown> extends CommonColDef<TData, number | string> {
	declare cellRendererParams: { numericFormat: NumberFormat };

	valueTypeGetter: (params: ValueGetterParams) => ({ value?: number | undefined; type: string } | undefined);
	constructor(
		params: ColDefWithFields<TData, number | string>
			& {
				valueTypeGetter: (params: ValueGetterParams<TData>) =>
				({ value?: number | undefined; type: string } | undefined);
			}
	) {
		super({
			type: 'numericColumn',
			minWidth: 78,
			maxWidth: 120,
			cellRenderer: NumericPercentageCellComponent,
			cellRendererParams: { numericFormat: 'generic' },
			...params
		});
		this.valueTypeGetter = params.valueTypeGetter;
	}

	override valueGetter = (params: ValueGetterParams) => {
		const typeValue = this.valueTypeGetter(params);
		if (typeValue) {
			const { type, value } = typeValue;
			if (type === 'percentage' && value !== undefined) {
				return formatNumber(value, type, '%');
			}
			return value;
		}
		return undefined;
	};
}

export class LinkMultiValueColDef<TData = unknown, TValue = unknown> extends CommonColDef<TData, TValue> {
	constructor(params: ColDefWithFields<TData, TValue>) {
		super({
			cellRenderer: LinkMultiValueCellComponent,
			suppressHeaderMenuButton: true,
			sortable: false,
			autoHeight: true,
			...params
		});
	}
}

export class MultiLineColDef<TData = unknown, TValue = unknown> extends CommonColDef<TData, TValue> {
	colDef: MultiLineValueTypes | undefined;
	constructor(params: ColDefWithFields<TData, TValue>) {
		super({
			cellRenderer: MultiLineValueCellComponent,
			sortable: false,
			autoHeight: true,
			columnType: ColumnType.TEXT_COLUMN,
			suppressHeaderMenuButton: false,
			...params
		});

		this.colDef = params.valueType;
	}

	override valueFormatter = (params: ValueFormatterParams) =>
		params.value && this.colDef === MultiLineValueTypes.DATE
			? moment(<string> params.value, getParseDate((<FilterDateFormatType> this.cellRendererParams).inputFormat))
				.format(getFormatDate((<FilterDateFormatType> this.cellRendererParams).outputFormat))
			: '';
}
export class CheckboxColDef<TData = unknown, TValue = unknown> extends CommonColDef<TData, TValue> {
	constructor(params: ColDefWithFields<TData, TValue>) {
		super({
			cellRenderer: CheckboxCellComponent,
			...params
		});
	}
}
export class IconMethodColDef<TData = unknown, TValue = unknown> extends CommonColDef<TData, TValue> {
	constructor(params: ColDefWithFields<TData, TValue>) {
		super({
			maxWidth: 32,
			minWidth: 32,
			width: 32,
			suppressSizeToFit: true,
			resizable: false,
			sortable: false,
			cellRenderer: IconMethodCellComponent,
			...params
		});
	}
}
